import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, EmptyState, Badge } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';

const PAGE_SIZE = 50;

const STATUS_MAP = { processing:'active', partial:'warning', fulfilled:'ok', backordered:'warning', cancelled:'critical' };

// QZ Tray signing — fetches signature from server
async function getQZSignature(request) {
  const resp = await fetch('/api/shipping/qz-sign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ request }),
  });
  const data = await resp.json();
  return data.signature;
}

// Set up QZ Tray security (certificate + signing)
async function setupQZSecurity() {
  if (!window.qz) return;
  try {
    const certResp = await fetch('/api/shipping/qz-cert');
    const cert = await certResp.text();
    window.qz.security.setCertificatePromise(function(resolve, reject) {
      resolve(cert);
    });
    window.qz.security.setSignatureAlgorithm('SHA512');
    window.qz.security.setSignaturePromise(function(toSign) {
      return function(resolve, reject) {
        getQZSignature(toSign).then(resolve).catch(reject);
      };
    });
  } catch (e) {
    console.warn('QZ security setup failed:', e.message);
  }
}

async function printViaQZ(printerName, data, isZpl = false) {
  if (!window.qz) throw new Error('QZ Tray not loaded');
  await setupQZSecurity();
  if (!window.qz.websocket.isActive()) {
    await window.qz.websocket.connect();
  }
  const config = isZpl
    ? window.qz.configs.create(printerName)
    : window.qz.configs.create(printerName, {
        size: { width: 8.5, height: 11 }, units: 'in',
        scaleContent: true, colorType: 'blackwhite',
        rasterize: true, density: 300,
      });
  const printData = isZpl
    ? [{ type: 'raw', format: 'plain', data, options: { language: 'ZPL' } }]
    : [{ type: 'pixel', format: 'base64', data, options: { copies: 1 } }];
  await window.qz.print(config, printData);
}

// Print shipment label via QZ Tray
async function printLabel(labelZpl, zebraPrinterIp, zebraPort) {
  // Try QZ Tray first, fall back to raw socket note
  try {
    if (window.qz) {
      const labelPrinterName = settings?.zebra_printer_name || 'ZDesigner ZT231';
      await printViaQZ(labelPrinterName, labelZpl, true);
      return;
    }
  } catch {}
  // Fallback: download ZPL
  const blob = new Blob([labelZpl], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'label.zpl';
  a.click();
}

export function ShipModal({ order, selections = {}, onSelectionChange, onClose, onSuccess, settings }) {
  const [form, setForm] = useState({
    recipientName:     order.shipping_name     || order.customer_name || '',
    recipientAddress1: order.shipping_address1 || '',
    recipientAddress2: order.shipping_address2 || '',
    recipientCity:     order.shipping_city     || '',
    recipientState:    order.shipping_state    || '',
    recipientZip:      order.shipping_zip      || '',
    recipientPhone:    order.shipping_phone    || '',
    weightLbs:         '',
    lengthIn:          '',
    widthIn:           '',
    heightIn:          '',
    shipMode:          'ground',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [multiShipInfo, setMultiShipInfo] = useState(null);

  // Compute box sizes from order item data (hazmat fields now included in order items)
  React.useEffect(() => {
    const items = order.items || [];
    if (!items.length) return;

    // Filter to selected HazMat items using data already on the order items
    const allCheckedItems = items.reduce((acc, item) => {
      const sel = selections[item.id];
      const hasFR = items.some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
      const itemWh = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
      const checked = sel?.checked !== undefined ? sel.checked : (hasFR ? itemWh > 256 : false);
      if (!checked) return acc;
      const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
      const availableQty = remaining > 0 ? remaining : (item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0));
      const shipQty = sel?.qty !== undefined ? parseInt(sel.qty) : availableQty;
      if (shipQty > 0) acc.push({ ...item, shipQty });
      return acc;
    }, []);
    const hazmatItems = allCheckedItems.filter(item =>
      item.hazmatEnabled === true || item.hazmatEnabled === 't' ||
      item.hazmat_enabled === true || item.hazmat_enabled === 't'
    );

    // If no hazmat info on items yet, fallback to products API
    const needsFetch = items.some(i => i.hazmatEnabled === undefined && i.hazmat_enabled === undefined);
    if (!hazmatItems.length && needsFetch) {
      const skus = items.map(i => i.sku).filter(Boolean);
      if (!skus.length) return;
      api.get(`/api/products?skus=${skus.join(',')}`).then(({ data }) => {
        const productList = Array.isArray(data) ? data : (data?.products || []);
        const fbItems = items.reduce((acc, item) => {
          const prod = productList.find(p => p.sku === item.sku);
          const isHazmat = prod && (prod.hazmat_enabled === true || prod.hazmat_enabled === 't') && prod.hazmat_un_number;
          if (!isHazmat) return acc;
          const sel = selections[item.id];
          const allHazmatWh = items.map(i => { const p2 = productList.find(p => p.sku === i.sku); return parseFloat(p2?.hazmat_wh_per_unit || 0); });
          const hasFR2 = allHazmatWh.some(wh => wh > 256);
          const itemWh2 = parseFloat(prod.hazmat_wh_per_unit || 0);
          const checked = sel?.checked !== undefined ? sel.checked : (hasFR2 ? itemWh2 > 256 : false);
          if (!checked) return acc;
          const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
          const shipQty = sel?.qty !== undefined ? parseInt(sel.qty) : remaining;
          if (shipQty > 0) acc.push({ ...item, hazmatWhPerUnit: prod.hazmat_wh_per_unit, weight: prod.weight, shipQty });
          return acc;
        }, []);
        applyBoxSize(fbItems, fbItems);
      }).catch(() => {});
      return;
    }

    applyBoxSize(hazmatItems, allCheckedItems);
  }, [order, selections]); // eslint-disable-line

  function applyBoxSize(hazmatItems, allItems) {
    if (!hazmatItems.length) return;
    const itemsForWeight = (allItems && allItems.length) ? allItems : hazmatItems;
    const totalQty    = hazmatItems.reduce((s, i) => s + i.shipQty, 0);
    const totalWeight = itemsForWeight.reduce((s, i) => s + (parseFloat(i.weight || i.hazmatWeight || 0)) * i.shipQty, 0);
    const maxWh = Math.max(0, ...hazmatItems.map(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0)));
    const is52Ah = maxWh > 500;
    const isLQ   = maxWh <= 256;

    const hasBundle = !isLQ && hazmatItems.some(i => (i.productName || '').toLowerCase().includes('bundle'));
    let l, w, h;
    if (hasBundle)           { l = 30;   w = 18;  h = 16; }
    else if (totalQty === 1) { l = 9;    w = 9;   h = 9;  }
    else if (totalQty <= 4)  { l = isLQ ? 13 : 16;   w = isLQ ? 13 : 16;  h = isLQ ? 13 : 16; }
    else if (totalQty <= 6)  {
      if (isLQ)              { l = 16;   w = 16;  h = 16; }
      else if (is52Ah)       { l = 28.5; w = 9.5; h = 14; }
      else                   { l = 28.5; w = 9.5; h = 11; }
    } else {
      if (is52Ah)            { l = 28.5; w = 9.5; h = 14; }
      else                   { l = 28.5; w = 9.5; h = 11; }
    }

    setForm(f => ({
      ...f,
      weightLbs: totalWeight > 0 ? totalWeight.toFixed(1) : f.weightLbs,
      lengthIn: l, widthIn: w, heightIn: h,
    }));
    setMultiShipInfo(totalQty > 6
      ? { totalQty, is52Ah, perBox: 6, boxes: Math.ceil(totalQty / 6) }
      : null);
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.weightLbs) { setError('Weight is required'); return; }
    if (!form.recipientAddress1 || !form.recipientCity || !form.recipientState || !form.recipientZip) {
      setError('Recipient address is required'); return;
    }
    setLoading(true); setError('');
    try {
      // Build selected items list
      const items = order.items || [];
      const selectedItems = items
        .filter(item => {
          const isHazmat = item.hazmatEnabled === true || item.hazmatEnabled === 't';
          const sel = selections[item.id];
          // Hazmat: included unless explicitly unchecked
          // Non-hazmat: only included if explicitly checked
          return isHazmat ? sel?.checked !== false : sel?.checked === true;
        })
        .map(item => {
          const sel = selections[item.id];
          const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
          return {
            salesOrderItemId: item.id,
            quantity: sel?.qty !== undefined ? sel.qty : remaining,
          };
        })
        .filter(i => i.quantity > 0);

      const { data } = await api.post('/api/shipping/create-label', {
        salesOrderId: order.id, ...form,
        weightLbs: parseFloat(form.weightLbs),
        lengthIn:  form.lengthIn ? parseFloat(form.lengthIn) : undefined,
        widthIn:   form.widthIn  ? parseFloat(form.widthIn)  : undefined,
        heightIn:  form.heightIn ? parseFloat(form.heightIn) : undefined,
        selectedItems,
      });
      onSuccess(data);
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--bg-primary, #ffffff)', borderRadius:12, padding:24, width:520,
        maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
        border:'1px solid var(--border)' }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:16 }}>Create Shipment — {order.shopify_order_number || order.order_ref}</h2>

        {multiShipInfo && (
          <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:8,
            padding:'10px 14px', marginBottom:12, fontSize:13, color:'#92400e' }}>
            <i className="ti ti-alert-triangle" style={{ marginRight:6 }} />
            <strong>{multiShipInfo.totalQty} batteries</strong> — requires <strong>{multiShipInfo.boxes} separate shipments</strong> (max 6 per box).
            This will create shipment 1 of {multiShipInfo.boxes}. Repeat for remaining batteries.
          </div>
        )}

        {/* Item selection — hazmat pre-checked, non-hazmat optional */}
        {(() => {
          const items = order.items || [];
          const hazmatItems = items.filter(i => i.hazmatEnabled === true || i.hazmatEnabled === 't');
          const nonHazmatItems = items.filter(i => i.hazmatEnabled !== true && i.hazmatEnabled !== 't' && i.fulfillableQuantity > 0);
          if (!nonHazmatItems.length) return null;
          return (
            <div style={{ border:'1px solid var(--border)', borderRadius:8, marginBottom:12, overflow:'hidden' }}>
              <div style={{ background:'var(--surface-2)', padding:'6px 12px', fontSize:11, fontWeight:600,
                color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                Items in this shipment
              </div>
              <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                <tbody>
                  {hazmatItems.map(item => {
                    const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                    const sel = selections[item.id];
                    // If any FR item (>256Wh) exists, default non-FR hazmat items to unchecked
                    const hasFR = hazmatItems.some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
                    const itemWh = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
                    const defaultChecked = hasFR ? itemWh > 256 : false;
                    const checked = sel?.checked !== undefined ? sel.checked : defaultChecked;
                    const qty = sel?.qty !== undefined ? sel.qty : (remaining > 0 ? remaining : item.quantity);
                    return (
                      <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'6px 12px', width:32 }}>
                          <input type="checkbox" checked={checked}
                            onChange={e => onSelectionChange(item.id, { checked: e.target.checked, qty })} />
                        </td>
                        <td style={{ padding:'6px 4px' }}>
                          <span style={{ fontWeight:500 }}>{item.sku}</span>
                          <span style={{ color:'var(--text-secondary)', marginLeft:6 }}>{item.productName}</span>
                        </td>
                        <td style={{ padding:'6px 4px' }}>
                          <span style={{ background:'#fef3c7', color:'#92400e', border:'1px solid #f59e0b',
                            borderRadius:4, padding:'1px 5px', fontSize:10, fontWeight:600 }}>HAZMAT</span>
                        </td>
                        <td style={{ padding:'6px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                          <input type="number" min="1" max={remaining > 0 ? remaining : item.quantity}
                            value={qty} style={{ width:48, textAlign:'center' }}
                            onChange={e => onSelectionChange(item.id, { checked, qty: parseInt(e.target.value) || 1 })} />
                          <span style={{ color:'var(--text-secondary)', marginLeft:4 }}>/ {remaining > 0 ? remaining : item.quantity}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {nonHazmatItems.map(item => {
                    const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                    const sel = selections[item.id];
                    const checked = sel?.checked === true; // default unchecked
                    const qty = sel?.qty !== undefined ? sel.qty : remaining;
                    return (
                      <tr key={item.id} style={{ borderBottom:'1px solid var(--border)', opacity: checked ? 1 : 0.6 }}>
                        <td style={{ padding:'6px 12px', width:32 }}>
                          <input type="checkbox" checked={checked}
                            onChange={e => onSelectionChange(item.id, { checked: e.target.checked, qty })} />
                        </td>
                        <td style={{ padding:'6px 4px' }}>
                          <span style={{ fontWeight:500 }}>{item.sku}</span>
                          <span style={{ color:'var(--text-secondary)', marginLeft:6 }}>{item.productName}</span>
                        </td>
                        <td style={{ padding:'6px 4px' }}>
                          <span style={{ color:'var(--text-tertiary)', fontSize:10 }}>optional</span>
                        </td>
                        <td style={{ padding:'6px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                          <input type="number" min="1" max={remaining}
                            value={qty} style={{ width:48, textAlign:'center' }}
                            disabled={!checked}
                            onChange={e => onSelectionChange(item.id, { checked, qty: parseInt(e.target.value) || 1 })} />
                          <span style={{ color:'var(--text-secondary)', marginLeft:4 }}>/ {remaining}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label>Ship Mode</label>
            <select value={form.shipMode} onChange={e => set('shipMode', e.target.value)}>
              <option value="ground">UPS Ground</option>
              <option value="air">UPS Air (Cargo Aircraft Only)</option>
            </select>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label>Recipient Name *</label>
            <input value={form.recipientName} onChange={e => set('recipientName', e.target.value)} />
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label>Address Line 1 *</label>
            <input value={form.recipientAddress1} onChange={e => set('recipientAddress1', e.target.value)} />
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label>Address Line 2</label>
            <input value={form.recipientAddress2} onChange={e => set('recipientAddress2', e.target.value)} />
          </div>
          <div className="form-group">
            <label>City *</label>
            <input value={form.recipientCity} onChange={e => set('recipientCity', e.target.value)} />
          </div>
          <div className="form-group">
            <label>State *</label>
            <input value={form.recipientState} onChange={e => set('recipientState', e.target.value)} maxLength={2} placeholder="OH" />
          </div>
          <div className="form-group">
            <label>ZIP *</label>
            <input value={form.recipientZip} onChange={e => set('recipientZip', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input value={form.recipientPhone} onChange={e => set('recipientPhone', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Weight (lbs) *</label>
            <input type="number" step="0.1" min="0.1" value={form.weightLbs} onChange={e => set('weightLbs', e.target.value)} />
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label>Dimensions (in) L × W × H</label>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              <input type="number" step="0.1" value={form.lengthIn} onChange={e => set('lengthIn', e.target.value)} placeholder="L" style={{ flex:1 }} />
              <span style={{ color:'var(--text-tertiary)' }}>×</span>
              <input type="number" step="0.1" value={form.widthIn} onChange={e => set('widthIn', e.target.value)} placeholder="W" style={{ flex:1 }} />
              <span style={{ color:'var(--text-tertiary)' }}>×</span>
              <input type="number" step="0.1" value={form.heightIn} onChange={e => set('heightIn', e.target.value)} placeholder="H" style={{ flex:1 }} />
            </div>
          </div>
        </div>

        {error && <div style={{ color:'var(--red)', fontSize:13, marginBottom:12 }}>{error}</div>}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}
            style={{ display:'flex', alignItems:'center', gap:8, minWidth:220, justifyContent:'center' }}>
            {loading && <span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,0.4)',
              borderTopColor:'white', borderRadius:'50%', animation:'spin 0.7s linear infinite',
              flexShrink:0 }}/>}
            {loading ? 'Creating shipment & printing…' : 'Create Shipment & Print Label'}
          </button>
        </div>
      </div>
    </div>
  );
}

async function pickPrinter(printers) {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:99999;display:flex;align-items:center;justify-content:center';
    const box = document.createElement('div');
    box.style.cssText = 'background:white;border-radius:10px;padding:24px;width:340px;box-shadow:0 20px 60px rgba(0,0,0,0.4)';
    box.innerHTML = '<div style="font-weight:600;font-size:15px;margin-bottom:16px">Select Printer</div>';
    printers.forEach(p => {
      const btn = document.createElement('button');
      btn.textContent = p;
      btn.style.cssText = 'display:block;width:100%;text-align:left;padding:10px 14px;margin-bottom:8px;border:1px solid #ddd;border-radius:6px;cursor:pointer;font-size:13px;background:white';
      btn.onmouseover = () => btn.style.background = '#f0f7ff';
      btn.onmouseout  = () => btn.style.background = 'white';
      btn.onclick = () => { document.body.removeChild(overlay); resolve(p); };
      box.appendChild(btn);
    });
    const cancel = document.createElement('button');
    cancel.textContent = 'Cancel';
    cancel.style.cssText = 'display:block;width:100%;padding:8px;border:none;cursor:pointer;font-size:12px;color:#666;margin-top:4px;background:transparent';
    cancel.onclick = () => { document.body.removeChild(overlay); resolve(null); };
    box.appendChild(cancel);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
  });
}

export function PrintResult({ result, shipmentId, order, onClose, onMarkPrinted, settings }) {
  const [labelPrinted, setLabelPrinted] = useState(false);
  const [formPrinted,  setFormPrinted]  = useState(false);
  const [slipPrinted,  setSlipPrinted]  = useState(false);
  const [printing, setPrinting] = useState('');
  const [autoPrinted, setAutoPrinted] = useState(false);

  // Auto-print label (and form for FR) on mount
  React.useEffect(() => {
    if (autoPrinted) return;
    setAutoPrinted(true);
    const autoPrint = async () => {
      // Print label
      try {
        const lp = settings?.zebra_printer_name || 'ZDesigner ZT231';
        await printViaQZ(lp, result.labelZpl, true);
        await api.post(`/api/shipping/${shipmentId}/printed`, { type: 'label' });
        setLabelPrinted(true);
        onMarkPrinted('label');
      } catch { /* silent fail — user can reprint manually */ }

      // Print packing slip ZPL to Zebra printer
      if (result.packingSlipPdf) {
        try {
          const lp = settings?.zebra_printer_name || 'ZDesigner ZT231';
          await Promise.race([
            printViaQZ(lp, result.packingSlipPdf, true),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
          ]);
          setSlipPrinted(true);
        } catch { /* silent fail */ }
      }

      // Brief pause before form so printer is ready
      await new Promise(r => setTimeout(r, 2000));

      // Print HazMat form for FR only
      if (!result.isLQ && result.hazmatFormPdf) {
        try {
          const printerName = settings?.hazmat_printer_name || 'HAZMAT Printer';
          await Promise.race([
            printViaQZ(printerName, result.hazmatFormPdf, false),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 30000))
          ]);
          await api.post(`/api/shipping/${shipmentId}/printed`, { type: 'form' });
          setFormPrinted(true);
          onMarkPrinted('form');
        } catch {
          // Silent fail — open PDF in browser as fallback
          try {
            const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
            const blob = new Blob([byteArr], { type: 'application/pdf' });
            window.open(URL.createObjectURL(blob), '_blank');
            await api.post(`/api/shipping/${shipmentId}/printed`, { type: 'form' });
            setFormPrinted(true);
            onMarkPrinted('form');
          } catch { /* truly silent */ }
        }
      }
    };
    autoPrint();
  }, []); // eslint-disable-line

  const printLabel = async () => {
    // Download ZPL file for label reprint
    if (result.labelZpl) {
      const blob = new Blob([result.labelZpl], { type: 'text/plain' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `label_${result.trackingNumber}.zpl`; a.click();
    }
  };

  const printForm = async () => {
    // Open form PDF in new tab for reprint
    if (result.hazmatFormPdf) {
      const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    }
    return;
    // eslint-disable-next-line no-unreachable
    setPrinting('form');
    try {
      await printViaQZ('', result.hazmatFormPdf, false);
      await api.post(`/api/shipping/${shipmentId}/printed`, { type: 'form' });
      setFormPrinted(true); onMarkPrinted('form');
    } catch (err) {
      // Fallback: open PDF
      const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
      await api.post(`/api/shipping/${shipmentId}/printed`, { type: 'form' });
      setFormPrinted(true); onMarkPrinted('form');
    } finally { setPrinting(''); }
  };

  // Auto-close when both label and form are printed (or LQ with just label)
  React.useEffect(() => {
    if (!autoPrinted) return;
    const needsForm = !result.isLQ && !!result.hazmatFormPdf;
    const isComplete = labelPrinted && (!needsForm || formPrinted);
    if (isComplete) {
      const timer = setTimeout(() => onClose(), 2000);
      return () => clearTimeout(timer);
    }
  }, [labelPrinted, formPrinted, autoPrinted, result.isLQ, onClose]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ background:'var(--surface-1)', borderRadius:12, padding:24, width:460,
        boxShadow:'0 20px 60px rgba(0,0,0,0.5)', position:'relative' }}>
        {printing && (
          <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.85)',
            borderRadius:12, zIndex:10, display:'flex', flexDirection:'column',
            alignItems:'center', justifyContent:'center', gap:12 }}>
            <span style={{ width:36, height:36, border:'4px solid #e5e7eb',
              borderTopColor:'#2563eb', borderRadius:'50%',
              animation:'spin 0.8s linear infinite', display:'block' }}/>
            <div style={{ fontWeight:600, fontSize:14, color:'#1e40af' }}>
              {printing === 'label' ? 'Sending label to printer…' : 'Sending form to printer…'}
            </div>
            <div style={{ fontSize:12, color:'#6b7280' }}>Please wait</div>
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--green)',
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <i className="ti ti-check" style={{ fontSize:20, color:'#fff' }} />
          </div>
          <div>
            <div style={{ fontWeight:600 }}>Shipment Created</div>
            <div style={{ fontSize:12, color:'var(--text-tertiary)', fontFamily:'monospace' }}>
              {result.trackingNumber}
            </div>
          </div>
        </div>

        <div style={{ background:'var(--surface-2)', borderRadius:8, padding:12, marginBottom:16, fontSize:13 }}>
          <div><strong>Order:</strong> {order.shopify_order_number || order.order_ref}</div>
          <div><strong>Tracking:</strong> {result.trackingNumber}</div>
          <div><strong>Description:</strong> {result.description}</div>
        </div>

        {/* Auto-print status */}
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
            background: labelPrinted ? '#dcfce7' : '#fef9c3', borderRadius:6, marginBottom:6, fontSize:13 }}>
            <i className={`ti ${labelPrinted ? 'ti-check' : 'ti-loader'}`}
              style={{ color: labelPrinted ? '#16a34a' : '#92400e' }}/>
            {labelPrinted ? 'Label sent to ZT231 ✓' : 'Sending label to ZT231…'}
          </div>
          {!result.isLQ && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
              background: formPrinted ? '#dcfce7' : '#fef9c3', borderRadius:6, fontSize:13 }}>
              <i className={`ti ${formPrinted ? 'ti-check' : 'ti-loader'}`}
                style={{ color: formPrinted ? '#16a34a' : '#92400e' }}/>
              {formPrinted ? 'HazMat form sent to printer ✓' : 'Sending HazMat form to printer…'}
            </div>
          )}
          {result.packingSlipPdf && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
              background: slipPrinted ? '#dcfce7' : '#fef9c3', borderRadius:6, marginBottom:6, fontSize:13 }}>
              <i className={`ti ${slipPrinted ? 'ti-check' : 'ti-loader'}`}
                style={{ color: slipPrinted ? '#16a34a' : '#92400e' }}/>
              {slipPrinted ? 'Packing slip sent to ZT231 ✓' : 'Sending packing slip to ZT231…'}
            </div>
          )}
          {result.isLQ && (
            <div style={{ padding:'8px 12px', background:'#f0fdf4', borderRadius:6, fontSize:12, color:'#166534' }}>
              <i className="ti ti-info-circle" style={{ marginRight:6 }}/>
              LQ shipment — no HazMat form required
            </div>
          )}
        </div>

        {/* Manual reprint buttons */}
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          <button className="btn btn-sm" onClick={printLabel} disabled={!!printing}
            style={{ position:'relative', minWidth:130 }}>
            {printing === 'label'
              ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid #fff',
                  borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite',
                  marginRight:6, verticalAlign:'middle' }}/>Sending to printer…</>
              : <><i className="ti ti-printer"/> Reprint Label</>}
          </button>
          {!result.isLQ && (
            <button className="btn btn-sm" onClick={printForm} disabled={!!printing}
              style={{ minWidth:130 }}>
              {printing === 'form'
                ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid #fff',
                    borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite',
                    marginRight:6, verticalAlign:'middle' }}/>Sending to printer…</>
                : <><i className="ti ti-file-text"/> Reprint Form</>}
            </button>
          )}
          {result.packingSlipPdf && (
            <button className="btn btn-sm" onClick={async () => {
              const lp = settings?.zebra_printer_name || 'ZDesigner ZT231';
              try { await printViaQZ(lp, result.packingSlipPdf, true); }
              catch {
                const blob = new Blob([result.packingSlipPdf], { type: 'text/plain' });
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                a.download = 'packing_slip.zpl'; a.click();
              }
            }}>
              <i className="ti ti-clipboard-list"/> Reprint Slip
            </button>
          )}
        </div>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          {labelPrinted && (result.isLQ || formPrinted)
            ? <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>Closing automatically…</span>
            : <span/>}
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default function HazMatShipping() {
  const [page, setPage]       = useState(0);
  const [status, setStatus]   = useState('processing');
  const [expanded, setExpanded] = useState(null);
  const [shipModal, setShipModal] = useState(null);
  const [printResult, setPrintResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [qzConnected, setQzConnected] = useState(false);
  // Item selection: { [orderId]: { [itemId]: { checked, qty } } }
  const [itemSelections, setItemSelections] = useState({});

  // Connect to QZ Tray on mount
  React.useEffect(() => {
    const connectQZ = async () => {
      try {
        if (!window.qz) return;
        await setupQZSecurity();
        if (!window.qz.websocket.isActive()) {
          await window.qz.websocket.connect();
        }
        setQzConnected(true);
      } catch {}
    };
    connectQZ();
    // Retry every 5s if not connected
    const iv = setInterval(async () => {
      if (window.qz && !window.qz.websocket.isActive()) {
        try { await window.qz.websocket.connect(); setQzConnected(true); } catch {}
      } else if (window.qz?.websocket.isActive()) {
        setQzConnected(true);
        clearInterval(iv);
      }
    }, 5000);
    return () => clearInterval(iv);
  }, []);
  const [shipmentMap, setShipmentMap] = useState({}); // orderId -> shipment

  // When filter is 'processing', also include 'partial' orders (may still have unfulfilled HazMat items)
  // For processing: fetch all (we filter client-side to split partial orders between views)
  // For fulfilled: fetch both fulfilled + partial (partial with no hazmat remaining shows here)
  // For other: pass status directly
  const statusFilter = (status === 'processing' || status === 'fulfilled') ? '' : status;
  const params = new URLSearchParams({ tags: 'NMC', hazmat: '1', limit: PAGE_SIZE, offset: page * PAGE_SIZE,
    ...(statusFilter && { status: statusFilter }) });
  const { data, loading, error, refetch } = useFetch(`/api/sales-orders?${params}`, [page, status]);
  const { data: settings } = useFetch('/api/shipping/settings');

  const allOrders = data?.orders || [];
  // Auto-load shipments for all visible orders
  React.useEffect(() => {
    allOrders.forEach(o => { if (!shipmentMap[o.id]) loadShipments(o.id); });
  }, [allOrders]); // eslint-disable-line

  // Helper: does this order have any hazmat items still shippable?
  const hasShippableHazmat = (o) => {
    const result = (o.items || []).some(i => {
      const isHazmat = i.hazmatEnabled === true || i.hazmatEnabled === 't' || i.hazmat_enabled === true || i.hazmat_enabled === 't';
      if (!isHazmat) return false;
      const remaining = i.fulfillableQuantity != null
        ? i.fulfillableQuantity
        : Math.max(0, (i.quantity || 0) - (i.fulfilledQuantity || 0));
      return remaining > 0;
    });
    return result;
  };

  // Processing view: partial/processing orders that still have hazmat items to ship
  // Fulfilled view: fulfilled orders + partial orders where hazmat is all done (fulfillableQty=0)
  const orders = status === 'processing'
    ? allOrders.filter(o => {
        if (o.status === 'fulfilled' || o.status === 'cancelled') return false;
        if (o.status === 'processing') return true;
        return hasShippableHazmat(o); // partial: only if hazmat items remain
      })
    : status === 'fulfilled'
    ? allOrders.filter(o => o.status === 'fulfilled' || (o.status === 'partial' && !hasShippableHazmat(o)))
    : allOrders;
  const total  = data?.total  || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const loadShipments = useCallback(async (orderId) => {
    try {
      const { data: shipments } = await api.get(`/api/shipping/order/${orderId}`);
      setShipmentMap(m => ({ ...m, [orderId]: shipments }));
    } catch {}
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [bulkPrintStatus, setBulkPrintStatus] = React.useState(null); // null | { current, total, orderRef, done }
  const [generatingManifest, setGeneratingManifest] = React.useState(false);
  const [unmanifested, setUnmanifested] = React.useState({ count: 0, shipments: [] });
  const [checkedManifestIds, setCheckedManifestIds] = React.useState(new Set());

  // Poll for unmanifested count
  const fetchUnmanifested = React.useCallback(async () => {
    try {
      const { data } = await api.get('/api/shipping/unmanifested');
      setUnmanifested(data);
    } catch {}
  }, []);

  React.useEffect(() => {
    fetchUnmanifested();
    const interval = setInterval(fetchUnmanifested, 30000);
    return () => clearInterval(interval);
  }, [fetchUnmanifested]);

  const generateManifest = async (shipmentIds) => {
    if (!shipmentIds?.length) return;
    setGeneratingManifest(true);
    try {
      const { data: result } = await api.post('/api/shipping/manifest', { shipmentIds });
      let printers = [settings?.manifest_printer_name || 'Brother MFC-L8900CDW series Printer'];
      try { printers = await window.qz.printers.find(); } catch {}
      const chosen = await pickPrinter(printers);
      if (chosen) {
        try {
          await printViaQZ(chosen, result.manifestPdf, false);
        } catch {
          const byteArr = Uint8Array.from(atob(result.manifestPdf), c => c.charCodeAt(0));
          const blob = new Blob([byteArr], { type: 'application/pdf' });
          window.open(URL.createObjectURL(blob), '_blank');
        }
      }
      // Clear checked shipments that were just manifested
      setCheckedManifestIds(prev => {
        const next = new Set(prev);
        shipmentIds.forEach(id => next.delete(id));
        return next;
      });
      fetchUnmanifested();
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setGeneratingManifest(false); }
  };

  const printManifest = async () => {
    // If specific shipments are checked, print only those; otherwise print all unmanifested
    const ids = checkedManifestIds.size > 0
      ? [...checkedManifestIds]
      : unmanifested.shipments.map(s => s.id);
    if (!ids.length) return;
    const count = ids.length;
    if (!window.confirm(`Print manifest for ${count} shipment${count > 1 ? 's' : ''}?`)) return;
    await generateManifest(ids);
  };

  const printSelectedLabels = async () => {
    const lp = settings?.zebra_printer_name || 'ZDesigner ZT231';
    // Build list of [orderId, shipmentId, orderRef] for selected orders
    const targets = [...selectedIds].map(orderId => {
      const allShipments = Array.isArray(shipmentMap[orderId]) ? shipmentMap[orderId] : (shipmentMap[orderId] ? [shipmentMap[orderId]] : []);
      const shipment = allShipments.find(s => s?.status !== 'voided') || allShipments[0];
      const order = orders.find(o => o.id === orderId);
      return shipment ? { orderId, shipmentId: shipment.id, orderRef: order?.shopify_order_number || order?.order_ref || orderId } : null;
    }).filter(Boolean);

    if (!targets.length) { alert('No shipped orders selected.'); return; }

    setBulkPrintStatus({ current: 0, total: targets.length, orderRef: '', done: false });

    let printed = 0, failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const { shipmentId, orderRef } = targets[i];
      setBulkPrintStatus({ current: i + 1, total: targets.length, orderRef, done: false });
      try {
        const { data } = await api.get(`/api/shipping/${shipmentId}/reprint`);
        await printViaQZ(lp, data.labelZpl, true);
        if (data.packingSlipZpl) {
          await new Promise(r => setTimeout(r, 1000));
          await printViaQZ(lp, data.packingSlipZpl, true);
        }
        printed++;
      } catch (err) {
        failed++;
        console.error(`[BulkPrint] Failed for shipment ${shipmentId}:`, err.message);
      }
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, 1500));
    }

    setBulkPrintStatus({ current: targets.length, total: targets.length, orderRef: '', done: true, printed, failed });
    setTimeout(() => setBulkPrintStatus(null), 4000);
  };

  const reprintLabel = async (shipment) => {
    try {
      // Fetch label ZPL + fresh packing slip from backend
      const { data } = await api.get(`/api/shipping/${shipment.id}/reprint`);

      // Get available printers from QZ Tray
      let printers = [];
      if (window.qz && window.qz.websocket.isActive()) {
        try { printers = await window.qz.printers.find(); } catch {}
      }
      if (!printers.length) {
        alert('QZ Tray is not connected. Please ensure QZ Tray is running and try again.');
        return;
      }

      // Let user pick printer
      const selected = await pickPrinter(printers);
      if (!selected) return;

      // Print label ZPL
      await printViaQZ(selected, data.labelZpl, true);

      // Print packing slip ZPL if available
      if (data.packingSlipZpl) {
        await new Promise(r => setTimeout(r, 1000)); // brief pause between jobs
        await printViaQZ(selected, data.packingSlipZpl, true);
      }
    } catch (err) {
      alert('Reprint failed: ' + (err?.response?.data?.error || err.message));
    }
  };

  const reprintForm = async (shipment) => {
    try {
      const { data: result } = await api.post('/api/shipping/hazmat-form', { shipmentId: shipment.id });
      const byteArr = Uint8Array.from(atob(result.hazmatFormPdf), c => c.charCodeAt(0));
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) { alert(errMsg(err)); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorBanner message={error} onRetry={refetch} />;

  const anySelected = selectedIds.size > 0;

  return (
    <div>
      <div className="page-header">
        <h1>HazMat Shipping</h1>
        <div className="page-actions" style={{ flexWrap:'wrap', gap:8 }}>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}>
            <option value="processing">Processing</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="">All statuses</option>
          </select>
          {selectedIds.size > 0 && (
            <button
              onClick={printSelectedLabels}
              disabled={!!bulkPrintStatus && !bulkPrintStatus.done}
              style={{ background:'#16a34a', color:'white', border:'none', borderRadius:6,
                padding:'6px 14px', cursor:'pointer', fontWeight:600, fontSize:13,
                display:'flex', alignItems:'center', gap:6 }}>
              <i className="ti ti-printer" /> Print Labels ({selectedIds.size})
            </button>
          )}
          <button
            onClick={printManifest}
            disabled={generatingManifest}
            style={{ background:'#2563eb', color:'white', border:'none', borderRadius:6,
              padding:'6px 14px', cursor:'pointer', fontWeight:600, fontSize:13,
              display:'flex', alignItems:'center', gap:6 }}>
            {generatingManifest
              ? <><span style={{ width:12, height:12, border:'2px solid rgba(255,255,255,0.4)',
                  borderTopColor:'white', borderRadius:'50%', animation:'spin 0.7s linear infinite',
                  display:'inline-block' }}/> Generating…</>
              : <><i className="ti ti-file-description" /> Print Manifest{checkedManifestIds.size > 0 ? ` (${checkedManifestIds.size} selected)` : unmanifested.count > 0 ? ` (${unmanifested.count})` : ''}</>}
          </button>
          <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /></button>
        </div>
      </div>

      <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:8,
        padding:'10px 14px', marginBottom:12, fontSize:13, color:'#92400e' }}>
        <i className="ti ti-alert-triangle" style={{ marginRight:6 }} />
        Orders tagged <strong>NMC</strong> — lithium battery shipments requiring HazMat compliance.
        {!qzConnected && <span style={{ marginLeft:8 }}>⚠ QZ Tray not connected — prints will download as files.</span>}
        {qzConnected && <span style={{ marginLeft:8, color:'#166534' }}>✓ QZ Tray connected</span>}
      </div>

      {bulkPrintStatus && (
        <div style={{ background: bulkPrintStatus.done && bulkPrintStatus.failed === 0 ? '#dcfce7'
            : bulkPrintStatus.done ? '#fef9c3' : '#eff6ff',
          border: `1px solid ${bulkPrintStatus.done && bulkPrintStatus.failed === 0 ? '#86efac'
            : bulkPrintStatus.done ? '#fde047' : '#93c5fd'}`,
          borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13,
          color: bulkPrintStatus.done && bulkPrintStatus.failed === 0 ? '#166534'
            : bulkPrintStatus.done ? '#713f12' : '#1e40af',
          display:'flex', alignItems:'center', gap:10 }}>
          {!bulkPrintStatus.done && (
            <span style={{ width:14, height:14, border:'2px solid rgba(30,64,175,0.3)',
              borderTopColor:'#1e40af', borderRadius:'50%', animation:'spin 0.7s linear infinite',
              display:'inline-block', flexShrink:0 }} />
          )}
          {bulkPrintStatus.done
            ? bulkPrintStatus.failed === 0
              ? <><i className="ti ti-circle-check" /> Done — {bulkPrintStatus.printed} label{bulkPrintStatus.printed !== 1 ? 's' : ''} printed successfully</>
              : <><i className="ti ti-alert-triangle" /> {bulkPrintStatus.printed} printed, {bulkPrintStatus.failed} failed — check console for details</>
            : <>Printing {bulkPrintStatus.current} of {bulkPrintStatus.total}: <strong>{bulkPrintStatus.orderRef}</strong></>
          }
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width:32 }}></th>
                <th>Order #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Tags</th>
                <th>Status</th>
                <th style={{ textAlign:'right' }}>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const allShipments = Array.isArray(shipmentMap[o.id]) ? shipmentMap[o.id] : (shipmentMap[o.id] ? [shipmentMap[o.id]] : []);
                const shipment = allShipments.find(s => s?.status !== 'voided') || allShipments[0];
                const hasLabel = !!shipment?.label_printed_at;
                return (
                  <React.Fragment key={o.id}>
                    <tr>
                      {/* Checkbox — only show after label printed */}
                      <td>
                        {hasLabel && (
                          <input type="checkbox" checked={selectedIds.has(o.id)}
                            onChange={() => {
                              toggleSelect(o.id);
                              // Also check/uncheck all child HazMat item checkboxes
                              const willCheck = !selectedIds.has(o.id);
                              const items = o.items || [];
                              const newSel = {};
                              items.forEach(item => {
                                const isHazmat = item.hazmatEnabled === true || item.hazmatEnabled === 't';
                                if (isHazmat) {
                                  const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                                  const availableQty = remaining > 0 ? remaining : (item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0));
                                  newSel[item.id] = { checked: willCheck, qty: availableQty };
                                }
                              });
                              setItemSelections(prev => ({ ...prev, [o.id]: { ...prev[o.id], ...newSel } }));
                            }} style={{ width:'auto' }} />
                        )}
                      </td>
                      <td style={{ fontFamily:'monospace', fontSize:12, cursor:'pointer' }}
                        onClick={() => { setExpanded(expanded === o.id ? null : o.id); loadShipments(o.id); }}>
                        {o.shopify_order_number && o.shopify_order_number !== o.order_ref
                          ? o.shopify_order_number
                          : o.order_ref?.replace(/^shopify-\d+-/, '#')}
                      </td>
                      <td>{o.customer_name || '—'}</td>
                      <td style={{ fontSize:12 }}>{o.order_date ? new Date(o.order_date).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize:11 }}>
                        {(o.tags || '').split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
                          <span key={i} style={{ display:'inline-block', marginRight:4, padding:'1px 7px',
                            borderRadius:100, fontSize:11, fontWeight:500,
                            background: t.includes('NMC') ? '#fef3c7' : 'var(--surface-2)',
                            color: t.includes('NMC') ? '#92400e' : 'var(--text-secondary)',
                            border: t.includes('NMC') ? '1px solid #f59e0b' : '1px solid var(--border)',
                          }}>{t}</span>
                        ))}
                      </td>
                      <td><Badge status={STATUS_MAP[o.status] || 'active'} label={o.status} /></td>
                      <td style={{ textAlign:'right' }}>{o.total_value ? fmt.currency(o.total_value) : '—'}</td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          {(() => {
                            const hasUnfulfilled = (o.items || []).length === 0 ||
                              (o.items || []).some(item => {
                                const isHazmat = item.hazmatEnabled === true || item.hazmatEnabled === 't';
                                const rem = item.fulfillableQuantity != null
                                  ? item.fulfillableQuantity
                                  : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                                return isHazmat && rem > 0;
                              });
                            const hasAnyShipment = allShipments.some(s => s.status !== 'voided');
                            if (!hasAnyShipment) {
                              return (
                                <button className="btn btn-sm btn-primary"
                                  onClick={() => {
                                  setShipModal({ order: o, selections: itemSelections[o.id] || {} });
                                  }}>
                                  <i className="ti ti-truck" /> Ship
                                </button>
                              );
                            }
                            return hasUnfulfilled ? (
                              <button className="btn btn-sm btn-primary"
                                onClick={() => {
                                  setShipModal({ order: o, selections: itemSelections[o.id] || {} });
                                }}>
                                <i className="ti ti-truck" /> Ship More
                              </button>
                            ) : null;
                          })()}
                        </div>
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr>
                        <td colSpan={8} style={{ background:'var(--surface-2)', padding:'8px 16px' }}>
                          {allShipments.length > 0 ? (
                            <div style={{ marginBottom:8 }}>
                              {allShipments.filter(s => s.status !== 'voided').map(s => {
                                const canManifest = s.ship_type === 'hazmat';
                                const isManifestChecked = checkedManifestIds.has(s.id);
                                return (
                                <div key={s.id} style={{ fontSize:12, display:'flex', alignItems:'center', gap:12,
                                  padding:'6px 0', borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
                                  {canManifest ? (
                                    <input type="checkbox" checked={isManifestChecked}
                                      onChange={e => setCheckedManifestIds(prev => {
                                        const next = new Set(prev);
                                        e.target.checked ? next.add(s.id) : next.delete(s.id);
                                        return next;
                                      })}
                                      title="Include in manifest"
                                      style={{ cursor:'pointer', flexShrink:0 }}/>
                                  ) : <span style={{ width:13, display:'inline-block', flexShrink:0 }}/>}
                                  <span style={{ opacity: s.status === 'voided' ? 0.5 : 1, textDecoration: s.status === 'voided' ? 'line-through' : 'none' }}>
                                    <strong>{s.tracking_number}</strong>
                                    &nbsp;|&nbsp;<strong>Mode:</strong> {s.ship_type || s.ship_mode}
                                    &nbsp;|&nbsp;<strong>Weight:</strong> {s.weight_lbs} lbs
                                    &nbsp;|&nbsp;<strong>Label:</strong> {s.label_printed_at ? new Date(s.label_printed_at).toLocaleString() : 'Not printed'}
                                    {s.ship_type === 'hazmat' && <>&nbsp;|&nbsp;<strong>Form:</strong> {s.form_printed_at ? new Date(s.form_printed_at).toLocaleString() : 'Not printed'}</>}
                                    {s.status === 'voided' && <span style={{ color:'var(--red)', marginLeft:8 }}>(Voided)</span>}
                                    {s.manifested_at && <span style={{ color:'var(--green)', marginLeft:8 }}>✓ Manifested</span>}
                                  </span>
                                  <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                                    <button className="btn btn-sm" onClick={() => reprintLabel(s)}>
                                      <i className="ti ti-printer"/> Label
                                    </button>
                                    {s.ship_type === 'hazmat' && (
                                      <button className="btn btn-sm" onClick={() => reprintForm(s)}>
                                        <i className="ti ti-file-text"/> Form
                                      </button>
                                    )}
                                    {s.status !== 'voided' && (
                                      <button className="btn btn-sm" style={{ color:'var(--red)' }}
                                        onClick={async () => {
                                          if (!window.confirm(`Void shipment ${s.tracking_number}?`)) return;
                                          try {
                                            await api.post(`/api/shipping/${s.id}/void`);
                                            loadShipments(o.id);
                                            refetch();
                                          } catch (err) { alert(errMsg(err)); }
                                        }}>
                                        <i className="ti ti-ban"/> Void
                                      </button>
                                    )}
                                  </div>
                                </div>
                                ); })}  {/* end shipments map */}
                            </div>
                          ) : <span style={{ color:'var(--text-tertiary)', fontSize:12 }}>No shipment created yet</span>}
                          {(o.items || []).length > 0 && (() => {
                            const sel = itemSelections[o.id] || {};
                            const setItemSel = (itemId, field, val) => {
                              setItemSelections(prev => ({
                                ...prev,
                                [o.id]: { ...prev[o.id], [itemId]: { ...(prev[o.id]?.[itemId] || {}), [field]: val } }
                              }));
                            };
                            return (
                              <table style={{ width:'100%', fontSize:12, marginTop:8 }}>
                                <thead><tr>
                                  <th style={{ padding:'4px 8px', width:28 }}></th>
                                  <th style={{ textAlign:'left', padding:'4px 8px' }}>SKU</th>
                                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Product</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Ordered</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Fulfilled</th>
                                  <th style={{ textAlign:'right', padding:'4px 8px' }}>Remaining</th>
                                  <th style={{ textAlign:'center', padding:'4px 8px' }}>Ship Qty</th>
                                  <th style={{ textAlign:'left', padding:'4px 8px' }}>Type</th>
                                </tr></thead>
                                <tbody>{(o.items || []).map((item, i) => {
                                  const remaining = item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0) - (item.fulfilledQuantity || 0);
                                  const isHazmat = true;
                                  const availableQty = remaining > 0 ? remaining : (item.fulfillableQuantity != null ? item.fulfillableQuantity : (item.quantity || 0));
                                  const canSelect = isHazmat && availableQty > 0;
                                  const itemSel = sel[item.id] || {};
                                  const wh2 = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
                                  const hasFR2 = (o.items || []).some(i => parseFloat(i.hazmatWhPerUnit || i.hazmat_wh_per_unit || 0) > 256);
                                  const checked = canSelect && (itemSel.checked !== undefined ? itemSel.checked : (hasFR2 ? wh2 > 256 : false));
                                  const shipQty = itemSel.qty !== undefined ? itemSel.qty : availableQty;
                                  const wh = parseFloat(item.hazmatWhPerUnit || item.hazmat_wh_per_unit || 0);
                                  const shipType = wh > 256 ? 'FR' : wh > 0 ? 'LQ' : '—';
                                  return (
                                    <tr key={i} style={{ background: remaining > 0 ? 'var(--amber-bg, #fef3c7)' : 'transparent',
                                      opacity: canSelect ? 1 : 0.5 }}>
                                      <td style={{ padding:'4px 8px', textAlign:'center' }}>
                                        {canSelect && (
                                          <input type="checkbox" checked={checked}
                                            onChange={e => setItemSel(item.id, 'checked', e.target.checked)}
                                            style={{ cursor:'pointer' }}/>
                                        )}
                                      </td>
                                      <td style={{ fontFamily:'monospace', padding:'4px 8px' }}>{item.sku}</td>
                                      <td style={{ padding:'4px 8px' }}>{item.productName}</td>
                                      <td style={{ textAlign:'right', padding:'4px 8px' }}>{item.quantity}</td>
                                      <td style={{ textAlign:'right', padding:'4px 8px', color:'var(--text-tertiary)' }}>{item.fulfilledQuantity || 0}</td>
                                      <td style={{ textAlign:'right', padding:'4px 8px', fontWeight: remaining > 0 ? 600 : 'normal',
                                        color: remaining > 0 ? '#92400e' : 'var(--text-tertiary)' }}>{remaining}</td>
                                      <td style={{ textAlign:'center', padding:'2px 8px' }}>
                                        {canSelect && checked && (
                                          <input type="number" min={1} max={Math.max(1, availableQty)} value={shipQty || 1}
                                            onChange={e => setItemSel(item.id, 'qty', Math.min(remaining, Math.max(1, parseInt(e.target.value)||1)))}
                                            style={{ width:50, textAlign:'center', padding:'2px 4px', border:'1px solid var(--border)', borderRadius:3, fontSize:11 }}/>
                                        )}
                                      </td>
                                      <td style={{ padding:'4px 8px' }}>
                                        {shipType !== '—' && (
                                          <span style={{ fontSize:10, padding:'1px 5px', borderRadius:3, background: shipType==='LQ' ? '#dcfce7' : '#fef3c7',
                                            color: shipType==='LQ' ? '#166534' : '#92400e', fontWeight:600 }}>{shipType}</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}</tbody>
                              </table>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!orders.length && <EmptyState icon="ti-truck" message="No HazMat orders found" />}
        </div>

        {totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:12, fontSize:13 }}>
            <span style={{ color:'var(--text-tertiary)' }}>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page+1)*PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div style={{ display:'flex', gap:4 }}>
              <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
              <button className="btn btn-sm" disabled={page === 0} onClick={() => setPage(p => p-1)}>‹</button>
              <span style={{ padding:'4px 10px', fontSize:12 }}>Page {page+1} of {totalPages}</span>
              <button className="btn btn-sm" disabled={page >= totalPages-1} onClick={() => setPage(p => p+1)}>›</button>
              <button className="btn btn-sm" disabled={page >= totalPages-1} onClick={() => setPage(totalPages-1)}>»</button>
            </div>
          </div>
        )}
      </div>

      {shipModal && (
        <ShipModal
          order={shipModal.order}
          selections={shipModal.selections}
          onSelectionChange={(itemId, sel) => {
            setItemSelections(prev => ({
              ...prev,
              [shipModal.order.id]: { ...(prev[shipModal.order.id] || {}), [itemId]: sel }
            }));
            setShipModal(m => m ? { ...m, selections: { ...m.selections, [itemId]: sel } } : m);
          }}
          onClose={() => setShipModal(null)}
          onSuccess={(result) => {
            setShipModal(null);
            setPrintResult({ result, order: shipModal.order });
            // Clear selections for this order after shipping
            setItemSelections(prev => { const n = {...prev}; delete n[shipModal.order.id]; return n; });
          }}
        />
      )}

      {printResult && (
        <PrintResult
          result={printResult.result}
          shipmentId={printResult.result.shipmentId}
          order={printResult.order}
          settings={settings}
          onClose={() => { setPrintResult(null); refetch(); fetchUnmanifested(); }}
          onMarkPrinted={(type) => {
            if (type === 'label') {
              loadShipments(printResult.order.id);
              // Auto-check FR (hazmat) shipments for manifest printing after label is printed
              if (!printResult.result.isLQ && printResult.result.shipmentId) {
                setCheckedManifestIds(prev => new Set([...prev, printResult.result.shipmentId]));
              }
            }
          }}
        />
      )}
    </div>
  );
}
